import {
  Badge,
  Box,
  Container,
  Flex,
  Heading,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Text,
  Tooltip,
  useColorModeValue,
  VStack,
} from "@chakra-ui/react"
import {
  createFileRoute,
  getRouteApi,
  useNavigate,
} from "@tanstack/react-router"
import { useCallback, useState } from "react"
import { FiPlus, FiSearch } from "react-icons/fi"
import Select from "@/components/Common/AppSelect"
import type { TechnologyResponseDTO } from "../../../../client"
import TechnologyList from "../../../../components/WMS/Technologies/TechnologyList"

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200] as const
const TECHNOLOGY_STATUS_OPTIONS = [
  { value: "active", label: "Aktywne" },
  { value: "inactive", label: "Nieaktywne" },
  { value: "archived", label: "Archiwalne" },
  { value: "all", label: "Wszystkie" },
] as const

type TechnologyStatusFilter =
  (typeof TECHNOLOGY_STATUS_OPTIONS)[number]["value"]

export const Route = createFileRoute(
  "/_layout/WMS/technologies/technologies-list",
)({
  validateSearch: (search: Record<string, unknown>) => {
    const rawPage =
      typeof search.page === "number"
        ? search.page
        : typeof search.page === "string"
          ? Number.parseInt(search.page, 10) || 1
          : 1
    const rawPageSize =
      typeof search.page_size === "number"
        ? search.page_size
        : typeof search.page_size === "string"
          ? Number.parseInt(search.page_size, 10) || 25
          : 25

    return {
      page: rawPage,
      page_size: PAGE_SIZE_OPTIONS.includes(
        rawPageSize as (typeof PAGE_SIZE_OPTIONS)[number],
      )
        ? rawPageSize
        : 25,
      status:
        typeof search.status === "string" &&
        TECHNOLOGY_STATUS_OPTIONS.some(
          (option) => option.value === search.status,
        )
          ? (search.status as TechnologyStatusFilter)
          : "active",
    }
  },
  component: TechnologyListPage,
})

const technologiesRouteApi = getRouteApi(
  "/_layout/WMS/technologies/technologies-list",
)

function TechnologyListPage() {
  const navigate = useNavigate()
  const search = technologiesRouteApi.useSearch() as {
    page: number
    page_size: number
    status: TechnologyStatusFilter
  }
  const [query, setQuery] = useState("")
  const [selected, setSelected] = useState<TechnologyResponseDTO[]>([])
  const panelBg = useColorModeValue("white", "gray.800")
  const panelBorder = useColorModeValue("gray.200", "whiteAlpha.200")
  const page = search.page > 0 ? search.page : 1
  const pageSize = search.page_size
  const status = search.status ?? "active"

  const handleSelectionChange = useCallback(
    (items: TechnologyResponseDTO[]) => {
      setSelected(items)
    },
    [],
  )

  return (
    <Container
      maxW="9xl"
      py={{ base: 4, lg: 3 }}
      h={{ base: "auto", lg: "full" }}
      minH={{ base: "auto", lg: 0 }}
      display={{ base: "block", lg: "flex" }}
      flexDirection={{ base: "column", lg: "column" }}
      overflow={{ base: "visible", lg: "hidden" }}
    >
      <VStack
        spacing={4}
        align="stretch"
        h={{ base: "auto", lg: "full" }}
        minH={{ base: "auto", lg: 0 }}
        flex="1"
      >
        <Box
          bgGradient="linear(to-r, teal.600, green.600)"
          color="white"
          borderRadius="2xl"
          px={{ base: 5, md: 8 }}
          py={{ base: 4, md: 5 }}
          boxShadow="lg"
        >
          <Flex
            direction={{ base: "column", md: "row" }}
            justify="space-between"
            align={{ base: "flex-start", md: "center" }}
            gap={4}
          >
            <Box>
              <Heading size="lg">Technologie</Heading>
              <Text mt={1} opacity={0.95}>
                Zarządzaj recepturami i ich wersjami dla procesów produkcyjnych.
              </Text>
            </Box>
            <Badge
              bg="whiteAlpha.250"
              color="white"
              borderRadius="full"
              px={3}
              py={1}
            >
              Zaznaczone: {selected.length}
            </Badge>
          </Flex>
        </Box>

        <Box
          bg={panelBg}
          borderWidth="1px"
          borderColor={panelBorder}
          borderRadius="2xl"
          p={{ base: 3, md: 4 }}
          boxShadow="sm"
          display="flex"
          flexDirection="column"
          flex="1"
          minH={0}
          overflow="hidden"
        >
          <Flex
            direction={{ base: "column", lg: "row" }}
            align={{ base: "stretch", lg: "center" }}
            justify="space-between"
            gap={3}
            mb={4}
          >
            <InputGroup maxW={{ base: "100%", lg: "520px" }}>
              <InputLeftElement pointerEvents="none">
                <FiSearch />
              </InputLeftElement>
              <Input
                size="sm"
                placeholder="Szukaj technologię"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                borderRadius="xl"
              />
            </InputGroup>
            <HStack spacing={2} justify={{ base: "flex-end", lg: "initial" }}>
              <Select
                size="sm"
                maxW={{ base: "100%", lg: "180px" }}
                value={status}
                onChange={(event) =>
                  navigate({
                    to: "/WMS/technologies/technologies-list",
                    search: {
                      page: 1,
                      page_size: pageSize,
                      status: event.target.value as TechnologyStatusFilter,
                    },
                  })
                }
                borderRadius="xl"
              >
                {TECHNOLOGY_STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
              <Select
                size="sm"
                maxW={{ base: "100%", lg: "170px" }}
                value={String(pageSize)}
                onChange={(event) =>
                  navigate({
                    to: "/WMS/technologies/technologies-list",
                    search: {
                      page: 1,
                      page_size: Number(event.target.value),
                      status,
                    },
                  })
                }
                borderRadius="xl"
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>
                    {size} / strona
                  </option>
                ))}
              </Select>
              <Tooltip label="Dodaj technologię" hasArrow placement="top">
                <IconButton
                  size="sm"
                  colorScheme="green"
                  aria-label="Dodaj technologię"
                  icon={<FiPlus />}
                  onClick={() =>
                    navigate({
                      to: "/WMS/technologies/create",
                      search: { clone_from: undefined, mode: undefined },
                    })
                  }
                  borderRadius="xl"
                />
              </Tooltip>
            </HStack>
          </Flex>

          <TechnologyList
            page={page}
            pageSize={pageSize}
            searchQuery={query}
            onChangePage={(nextPage) =>
              navigate({
                to: "/WMS/technologies/technologies-list",
                search: {
                  page: nextPage,
                  page_size: pageSize,
                  status,
                },
              })
            }
            onSelectionChange={handleSelectionChange}
            statusFilter={status}
          />
        </Box>
      </VStack>
    </Container>
  )
}
